import { AgGridTable, AgGridTableRef } from '@shared/components/ui/AgGridTable';
import { ColDef } from 'ag-grid-community';
import React, { useCallback, useRef, useState } from 'react';
import { useAgGridColumns } from '../../hooks/useAgGridColumns';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBagShopping } from '@fortawesome/pro-light-svg-icons';
import { Order } from '../../models/Orders';
import {
  ProductLine,
  ProductsModal,
  ProductsModalRef,
} from '../modals';

interface OrdersAgGridTableProps {
  orders: Order[];
  isLoading?: boolean;
  isFetching?: boolean;
  className?: string;
  refetch?: () => void;
  selectedColumns?: string[];
  columnDefs: ColDef[];
  fetchNextPage?: () => void;
  hasNextPage?: boolean;
  isFetchingNextPage?: boolean;
  sorting?: { field: string; direction: 'asc' | 'desc' } | null;
  onSortingChange?: (
    sorting: { field: string; direction: 'asc' | 'desc' } | null
  ) => void;
}

export const OrdersAgGridTable = React.memo(function OrdersAgGridTable({
  orders,
  isFetching = false,
  refetch,
  selectedColumns = [],
  columnDefs,
  fetchNextPage,
  hasNextPage,
  isFetchingNextPage,
  sorting,
  onSortingChange,
}: OrdersAgGridTableProps) {
  const agGridRef = useRef<AgGridTableRef>(null);

  // 🎯 El sorting ahora se maneja automáticamente por AgGridTable
  // No necesitamos lógica manual aquí

  // 🎯 Evitar crash: handler no-op para onGridReady
  const handleGridReady = useCallback(() => { }, []);

  // Estado y referencia para ProductsModal
  const productsModalRef = useRef<ProductsModalRef>(null);
  const [modalData, setModalData] = useState<{
    orderNumber: string;
    stockMessage: string;
    products: ProductLine[];
    subtotal: number;
    shipping: number;
    discount: number;
    total: number;
  } | null>(null);

  const handleOpenProductsModal = useCallback((order: Order) => {
    setModalData({
      orderNumber: order.ref,
      stockMessage:
        'Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua.',
      products: order.lines.map((line) => ({
        name: line.name || (line.pack ? line.pack.title : ''),
        image: line.product?.image_hash || '',
        badge: line.product?.code || (line.pack ? line.pack.title : ''),
        qty: line.qty,
        price: line.amount,
        subtotal: line.qty * line.amount,
      })),
      subtotal: order.lines.reduce(
        (acc, line) => acc + line.qty * line.amount,
        0
      ),
      shipping: order.total_shipping,
      discount: order.total_discount,
      total: order.total,
    });
    setTimeout(() => productsModalRef.current?.open(), 0);
  }, []);

  const ProductsCell = ({ order, onOpenModal }: { order: Order; onOpenModal: (order: Order) => void }) => {
    const qty = order.lines?.[0]?.qty ?? 0;
    const color = qty > 0 ? 'bg-success' : 'bg-error';

    return (
      <span className="text-sm font-medium flex items-center gap-2 relative">
        <button
          type="button"
          className="relative inline-flex items-center gap-2 focus:outline-none hover:opacity-70"
          onClick={() => onOpenModal(order)}
          title="Ver productos"
        >
          <span className="relative inline-block">
            <FontAwesomeIcon icon={faBagShopping} className="w-3 h-3" />
            <span
              className={`absolute -top-1 -right-0 w-1.5 h-1.5 mt-2.5 rounded-full border border-black ${color}`}
            ></span>
          </span>
          {order.lines?.length || 0}
        </button>
      </span>
    );
  };

  const columnDefsWithRenderers: ColDef[] = columnDefs.map(col => {
    if (col.field === 'products') {
      return {
        ...col,
        cellRenderer: (params: any) => (
          <ProductsCell order={params.data} onOpenModal={handleOpenProductsModal} />
        )
      };
    }
    return col;
  });

  return (
    <div
      className={`w-full ${isFetching && !isFetchingNextPage ? 'opacity-50 pointer-events-none' : ''}`}
    >
      <AgGridTable
        ref={agGridRef}
        data={orders}
        columnDefs={columnDefsWithRenderers}
        height="calc(100vh - 300px)"
        enableSelection={false} // 🎯 OrdersShipped NO tiene selección múltiple
        onGridReady={handleGridReady}
        className="rounded-lg shadow-sm"
        fetchNextPage={fetchNextPage}
        hasNextPage={hasNextPage}
        isFetchingNextPage={isFetchingNextPage}
        gridOptions={{}} // Pasamos un objeto vacío para evitar conflictos
        sorting={sorting}
        onSortingChange={onSortingChange}
      />

      {/* Modal de productos */}
      {modalData && (
        <ProductsModal
          ref={productsModalRef}
          orderNumber={modalData.orderNumber}
          stockMessage={modalData.stockMessage}
          products={modalData.products}
          subtotal={modalData.subtotal}
          shipping={modalData.shipping}
          discount={modalData.discount}
          total={modalData.total}
        />
      )}
    </div>
  );
});

